import moment from 'moment';
import typeCheck from 'common/util/typeCheck';
import {
  getHouseholdEarliestApplicationStatus,
} from 'common/householdApplication/applicationHelpers';
import {
  HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  HOUSEHOLD_APPLICATION_STATUS_STARTED,
  HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  HOUSEHOLD_APPLICATION_STATUS_PAID,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_DOCUMENTS,
  HOUSEHOLD_APPLICATION_STATUS_REVIEWING_DOCUMENTS,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_VERIFICATION,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_TRANSUNION,
  HOUSEHOLD_APPLICATION_STATUS_DELIVERED,
  HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD,
  HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  HOUSEHOLD_APPLICATION_STATUS_DEPOSIT_PAID,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_DRAFT_PENDING,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SENT,
  HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
  HOUSEHOLD_LISTING_STATUS_ARCHIVED,
  HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED,
  HOUSEHOLD_APPLICATION_ARCHIVED_REASON_TO_LABEL_MAPPING,
  HOUSEHOLD_REJECTED_REASON_OPTION_ITEMS,
} from 'common/householdConstants';
import {
  SR_API_ACTION_PARTNER_GET_LISTING_HOUSEHOLDS_INTERESTED,
} from 'api/actionTypes';

const APPLICATION_FUNNEL_STEP_STATUS_COMPLETED = 'completed';
const APPLICATION_FUNNEL_STEP_STATUS_IN_PROGRESS = 'inProgress';
const APPLICATION_FUNNEL_STEP_STATUS_NOT_STARTED = 'notStarted';
const APPLICATION_FUNNEL_STEP_STATUS_REJECTED = 'rejected';

const APPLICATION_STATUS_TO_FUNNEL_CARD_STEP_STATUS_MAPPING = {
  [HOUSEHOLD_APPLICATION_STATUS_REQUESTED]: HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  [HOUSEHOLD_APPLICATION_STATUS_STARTED]: HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  [HOUSEHOLD_APPLICATION_STATUS_COMPLETED]: HOUSEHOLD_APPLICATION_STATUS_REQUESTED,
  [HOUSEHOLD_APPLICATION_STATUS_PAID]: HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  [HOUSEHOLD_APPLICATION_STATUS_PENDING_DOCUMENTS]: HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  [HOUSEHOLD_APPLICATION_STATUS_REVIEWING_DOCUMENTS]: HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  [HOUSEHOLD_APPLICATION_STATUS_PENDING_VERIFICATION]: HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  [HOUSEHOLD_APPLICATION_STATUS_PENDING_TRANSUNION]: HOUSEHOLD_APPLICATION_STATUS_COMPLETED,
  [HOUSEHOLD_APPLICATION_STATUS_DELIVERED]: HOUSEHOLD_APPLICATION_STATUS_DELIVERED,
  [HOUSEHOLD_APPLICATION_STATUS_PENDING_LANDLORD]: HOUSEHOLD_APPLICATION_STATUS_DELIVERED,
  [HOUSEHOLD_APPLICATION_STATUS_ACCEPTED]: HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  [HOUSEHOLD_APPLICATION_STATUS_DEPOSIT_PAID]: HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  [HOUSEHOLD_APPLICATION_STATUS_LEASE_DRAFT_PENDING]: HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  [HOUSEHOLD_APPLICATION_STATUS_LEASE_SENT]: HOUSEHOLD_APPLICATION_STATUS_ACCEPTED,
  [HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED]: HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED,
};

const HOUSEHOLD_APPLICATION_STATUS_TO_FUNNEL_CARD_LABEL_MAPPING = {
  [HOUSEHOLD_APPLICATION_STATUS_REQUESTED]: 'Interested',
  [HOUSEHOLD_APPLICATION_STATUS_COMPLETED]: 'Applied',
  [HOUSEHOLD_APPLICATION_STATUS_DELIVERED]: 'Credit / Background Checked',
  [HOUSEHOLD_APPLICATION_STATUS_ACCEPTED]: 'Approved',
  [HOUSEHOLD_APPLICATION_STATUS_LEASE_SIGNED]: 'Signed Lease!',
};

const HOUSEHOLD_APPLICATION_FUNNEL_CARD_STEPS = Object.keys(
  HOUSEHOLD_APPLICATION_STATUS_TO_FUNNEL_CARD_LABEL_MAPPING
);

export default async function getHouseholdsInterested({ state, apiClient }, { listingId }) {
  typeCheck('listingId::PositiveNumber', listingId);
  return apiClient.postWithState({
    action: SR_API_ACTION_PARTNER_GET_LISTING_HOUSEHOLDS_INTERESTED,
    state: state,
    payload: {
      listingId,
    },
    onSuccess(householdsInterested) {
      typeCheck('householdsInterested::Array', householdsInterested);

      householdsInterested.forEach((household) => {
        const householdListing = household.householdListings.find((householdListing) => {
          return householdListing.listingId === listingId;
        });
        const applicationMatches = household.householdApplications.filter((application) => {
          return application.listingId === listingId;
        });

        const firstApplicationCompletedDate = (
          applicationMatches.reduce((dateMatch, { completedDate }) => {
            if (!completedDate) {
              return dateMatch;
            }
            if (!dateMatch) {
              return completedDate || null;
            }
            return moment(dateMatch).isBefore(completedDate)
              ? dateMatch
              : completedDate;
          }, null)
          || household.createdDate
        );

        const isArchived = householdListing.status === HOUSEHOLD_LISTING_STATUS_ARCHIVED;

        const isRejected = (
          isArchived
          && householdListing.cancelReason === HOUSEHOLD_APPLICATION_ARCHIVE_REASON_REJECTED
        );

        const earliestApplicationStatus = APPLICATION_STATUS_TO_FUNNEL_CARD_STEP_STATUS_MAPPING[
          getHouseholdEarliestApplicationStatus(applicationMatches)
        ];

        const steps = HOUSEHOLD_APPLICATION_FUNNEL_CARD_STEPS.map((applicationStatus) => ({
          applicationStatus: applicationStatus,
          label: HOUSEHOLD_APPLICATION_STATUS_TO_FUNNEL_CARD_LABEL_MAPPING[applicationStatus],
          stepStatus: isRejected
            ? APPLICATION_FUNNEL_STEP_STATUS_COMPLETED
            : APPLICATION_FUNNEL_STEP_STATUS_NOT_STARTED,
          subLabel: null,
          tooltip: null,
        }));

        const cancelReasonLabel = HOUSEHOLD_APPLICATION_ARCHIVED_REASON_TO_LABEL_MAPPING[
          householdListing.cancelReason
        ] || householdListing.cancelReason || null;

        let stepCompletedFound = false;
        for (let i = steps.length - 1; i >= 0; i -= 1) {
          const currentStep = steps[i];
          const lastStep = steps[i + 1];

          const isCompleted = currentStep.applicationStatus === earliestApplicationStatus;
          if (
            currentStep.applicationStatus === HOUSEHOLD_APPLICATION_STATUS_ACCEPTED
            && isRejected
          ) {
            currentStep.stepStatus = APPLICATION_FUNNEL_STEP_STATUS_REJECTED;

            const rejectReasonOptionItem = HOUSEHOLD_REJECTED_REASON_OPTION_ITEMS.find(({ value }) => {
              return value === householdListing.rejectedReason;
            });

            currentStep.stepStatus = APPLICATION_FUNNEL_STEP_STATUS_REJECTED;
            currentStep.label = 'Rejected';
            currentStep.tooltip = rejectReasonOptionItem
              ? rejectReasonOptionItem.label
              : householdListing.rejectedReason || cancelReasonLabel;

            if (lastStep) {
              lastStep.stepStatus = APPLICATION_FUNNEL_STEP_STATUS_NOT_STARTED;
              break;
            }
          }
          else if (isCompleted) {
            stepCompletedFound = true;
            if (isArchived) {
              currentStep.stepStatus = APPLICATION_FUNNEL_STEP_STATUS_REJECTED;
              currentStep.tooltip = cancelReasonLabel;
            }
            else {
              currentStep.stepStatus = APPLICATION_FUNNEL_STEP_STATUS_COMPLETED;
              if (lastStep) {
                lastStep.stepStatus = APPLICATION_FUNNEL_STEP_STATUS_IN_PROGRESS;
                lastStep.subLabel = '(in progress)';
              }
            }
          }
          else if (stepCompletedFound) {
            currentStep.stepStatus = APPLICATION_FUNNEL_STEP_STATUS_COMPLETED;
          }
        }

        const stepsCompletedCount = steps
          .filter(({ stepStatus }) => stepStatus === APPLICATION_FUNNEL_STEP_STATUS_COMPLETED)
          .length;

        const showDownloadDocumentsLink = !!steps.find(({ applicationStatus, stepStatus, label }) => (
          applicationStatus === HOUSEHOLD_APPLICATION_STATUS_ACCEPTED
          && stepStatus === APPLICATION_FUNNEL_STEP_STATUS_COMPLETED
          && label === 'Approved'
        ));

        Object.assign(household, {
          steps,
          stepsCompletedCount,
          showDownloadDocumentsLink,
          firstApplicationCompletedDate,
        });
      });

      householdsInterested.sort((a, b) => b.stepsCompletedCount - a.stepsCompletedCount);

      state.set(
        ['partners', 'householdsInterestedByListingIds', String(listingId)],
        householdsInterested,
      );
      return householdsInterested;
    },
  });
}
